/**
 * Mobile drawer: brand + promos mega menu sub-panels (Figma 136:1140).
 */
(function () {
  const ACTIVE_CLASS = 'is-active';
  const OFF_LEFT_CLASS = 'is-off-left';
  const OFF_RIGHT_CLASS = 'is-off-right';
  const TRANSITION_MS = 400;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getDrawer() {
    return document.querySelector('#nav-drawer');
  }

  function getMainView(drawer) {
    return drawer?.querySelector('[data-mobile-nav-view="main"]');
  }

  function getActivePanel(drawer) {
    return drawer?.querySelector('[data-mobile-mega-panel].is-active');
  }

  function onTransitionEnd(element, callback) {
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      element.removeEventListener('transitionend', handler);
      callback();
    };

    const handler = (event) => {
      if (event.target !== element || event.propertyName !== 'transform') return;
      finish();
    };

    element.addEventListener('transitionend', handler);
    window.setTimeout(finish, TRANSITION_MS + 50);
  }

  function openFirstAccordion(panel) {
    const accordion = panel.querySelector('[data-collapsible-single]');
    if (!accordion) return;

    const hasActiveSection = accordion.querySelector(
      '[data-collapsible-trigger].is-expanded, [data-collapsible-container].is-expanded'
    );
    if (hasActiveSection) return;

    const firstTrigger = accordion.querySelector('[data-collapsible-trigger]');
    const firstContainerId = firstTrigger?.getAttribute('aria-controls');
    const firstContainer = firstContainerId ? document.getElementById(firstContainerId) : null;
    const firstContent = firstContainer?.querySelector('[data-collapsible-content]');

    if (!firstTrigger || !firstContainer || !firstContent) return;

    firstTrigger.classList.add('is-expanded');
    firstTrigger.setAttribute('aria-expanded', 'true');
    firstContainer.classList.add('is-expanded');
    firstContainer.setAttribute('aria-expanded', 'true');
    firstContainer.style.height = `${firstContent.offsetHeight}px`;

    window.setTimeout(() => {
      if (firstContainer.classList.contains('is-expanded')) {
        firstContainer.style.height = 'auto';
      }
    }, 500);
  }

  function resetAccordions(drawer) {
    drawer.querySelectorAll('[data-mobile-mega-panel] [data-collapsible-trigger].is-expanded').forEach((trigger) => {
      const dropdownId = trigger.getAttribute('aria-controls');
      const dropdown = dropdownId ? document.getElementById(dropdownId) : null;

      trigger.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');

      if (!dropdown) return;

      dropdown.classList.remove('is-expanded');
      dropdown.setAttribute('aria-expanded', 'false');
      dropdown.style.height = '';
    });
  }

  function resetViews(drawer) {
    const mainView = getMainView(drawer);
    if (!mainView) return;

    resetAccordions(drawer);

    drawer.querySelectorAll('[data-mobile-mega-panel]').forEach((panel) => {
      panel.hidden = true;
      panel.classList.remove(ACTIVE_CLASS, OFF_LEFT_CLASS, OFF_RIGHT_CLASS);
    });

    mainView.hidden = false;
    mainView.classList.add(ACTIVE_CLASS);
    mainView.classList.remove(OFF_LEFT_CLASS, OFF_RIGHT_CLASS);
  }

  function showMainView(drawer, animate = true) {
    const mainView = getMainView(drawer);
    const activePanel = getActivePanel(drawer);

    if (!mainView) return;

    if (!animate || !activePanel || prefersReducedMotion()) {
      resetViews(drawer);
      return;
    }

    mainView.hidden = false;
    mainView.classList.remove(ACTIVE_CLASS);
    mainView.classList.add(OFF_LEFT_CLASS);

    activePanel.classList.remove(ACTIVE_CLASS);
    activePanel.classList.add(OFF_RIGHT_CLASS);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        mainView.classList.remove(OFF_LEFT_CLASS);
        mainView.classList.add(ACTIVE_CLASS);
      });
    });

    onTransitionEnd(activePanel, () => {
      activePanel.hidden = true;
      activePanel.classList.remove(OFF_RIGHT_CLASS);
    });
  }

  function showMegaPanel(drawer, panelId) {
    const mainView = getMainView(drawer);
    const panel = drawer.querySelector(`#${CSS.escape(panelId)}`);

    if (!mainView || !panel) return;

    if (prefersReducedMotion()) {
      mainView.hidden = true;
      mainView.classList.remove(ACTIVE_CLASS, OFF_LEFT_CLASS, OFF_RIGHT_CLASS);

      panel.hidden = false;
      panel.classList.add(ACTIVE_CLASS);
      panel.classList.remove(OFF_LEFT_CLASS, OFF_RIGHT_CLASS);

      const subbody = panel.querySelector('.drawer__nav-subbody');
      if (subbody) subbody.scrollTop = 0;
      openFirstAccordion(panel);
      return;
    }

    panel.hidden = false;
    panel.classList.remove(OFF_RIGHT_CLASS);
    panel.getBoundingClientRect();

    mainView.classList.remove(ACTIVE_CLASS);
    mainView.classList.add(OFF_LEFT_CLASS);

    requestAnimationFrame(() => {
      panel.classList.add(ACTIVE_CLASS);
    });

    onTransitionEnd(panel, () => {
      mainView.hidden = true;
      mainView.classList.remove(OFF_LEFT_CLASS);

      const subbody = panel.querySelector('.drawer__nav-subbody');
      if (subbody) subbody.scrollTop = 0;
      openFirstAccordion(panel);
    });
  }

  function bindPromoCarousels(drawer) {
    drawer.querySelectorAll('[data-mobile-mega-promos-scroll]').forEach((scroller) => {
      if (scroller.dataset.promoScrollBound) return;
      scroller.dataset.promoScrollBound = 'true';

      let startX = 0;
      let startY = 0;

      scroller.addEventListener(
        'touchstart',
        (event) => {
          startX = event.touches[0].pageX;
          startY = event.touches[0].pageY;
        },
        { passive: true }
      );

      scroller.addEventListener(
        'touchmove',
        (event) => {
          const deltaX = Math.abs(event.touches[0].pageX - startX);
          const deltaY = Math.abs(event.touches[0].pageY - startY);

          if (deltaX > deltaY && deltaX > 5) {
            event.stopPropagation();
          }
        },
        { passive: true }
      );
    });
  }

  function bindDrawer(drawer) {
    if (!drawer || drawer.dataset.mobileMegaReady) return;
    drawer.dataset.mobileMegaReady = 'true';

    bindPromoCarousels(drawer);

    drawer.addEventListener('click', (event) => {
      const openTrigger = event.target.closest('[data-mobile-mega-open]');
      if (openTrigger) {
        event.preventDefault();
        const panelId = openTrigger.getAttribute('data-mobile-mega-open');
        if (panelId) showMegaPanel(drawer, panelId);
        return;
      }

      const backTrigger = event.target.closest('[data-mobile-mega-back]');
      if (backTrigger) {
        event.preventDefault();
        showMainView(drawer);
      }
    });

    drawer.querySelectorAll('[data-drawer-toggle]').forEach((button) => {
      button.addEventListener('click', () => {
        requestAnimationFrame(() => {
          if (!drawer.classList.contains('is-open')) {
            resetViews(drawer);
          }
        });
      });
    });

    document.addEventListener('theme:drawer:closing', () => {
      if (drawer.classList.contains('is-open')) {
        resetViews(drawer);
      }
    });
  }

  function init() {
    const drawer = getDrawer();
    if (drawer) bindDrawer(drawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
